import React, { useEffect, useState } from 'react';
import { Container, Button } from 'react-bootstrap';
import { useGetChatDetailsQuery } from 'services';
import { useNavigate, useParams } from 'react-router-dom';
import useChatStore, {
	setChatMessages,
	setReceiverDetails,
	setRefetchChatDetailsQuery,
} from 'store/chat';
import Chat from 'components/Chat';
import ChatMessages from 'components/Chat/ChatMessage/ChatMessages';
import MessageForm from 'components/Chat/MessageForm';
import { dataLimits } from 'constants';
import ChatList from 'components/Chat/ChatList/ChatList';
import { isDesktop, isMobileOnly, isTablet, useMobileOrientation } from 'react-device-detect';
import useAuthStore from 'store/auth';
import useTranslate from 'hooks/useTranslate';
import './styles.scss';

const ChatBox: React.FC = () => {
	const { translate } = useTranslate();
	const { receiverId, roomId = '' } = useParams<string>();
	const navigate = useNavigate();
	const { isLandscape } = useMobileOrientation();
	const [currentPage, setCurrentPage] = useState(1);
	const { chatMessages, receiverDetails }: Record<string, any> = useChatStore(
		(state) => state || [],
	);
	const { user: authUser }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});

	const {
		data: {
			docs: {
				results: messageList = [],
				receiverDetails: receiver = {},
				isBlock = false,
				blockUserIds = [],
				isReported = false,
			} = {},
			meta = {},
		} = {},
		refetch,
		isPending,
	}: any = useGetChatDetailsQuery({
		params: `receiverId=${receiverId}&order=DESC&page=${currentPage}&limit=${dataLimits?.chatMessages}`,
		receiverId: false,
	});

	useEffect(() => {
		if (refetch) setRefetchChatDetailsQuery(refetch);
	}, [refetch]);

	useEffect(() => {
		if (messageList && !isPending) {
			setChatMessages(currentPage > 1 ? [...messageList, ...chatMessages] : messageList);
			setReceiverDetails(receiver);
		}
	}, [messageList, isPending, receiver]);

	useEffect(() => {
		if (roomId) {
			if (currentPage > 1) setCurrentPage(1);
			else refetch();
		}
	}, [roomId]);

	useEffect(() => {
		if (currentPage > 1) {
			refetch();
		}
	}, [currentPage]);

	const checkBlockBySelf = () => {
		return isBlock && blockUserIds?.includes(Number(authUser?.userId)) ? true : false;
	};

	return (
		<div className="py-4">
			<Container fluid>
				<div className="chat-box d-flex gap-3 gap-xxl-4">
					{(isDesktop || (isTablet && isLandscape)) && <ChatList />}
					{(isMobileOnly || (isTablet && !isLandscape)) && (
						<Button
							className="back-chat-list p-0 bg-transparent border-0"
							onClick={() => navigate('/chat-list')}
						>
							<i className="ri-arrow-left-line"></i> {translate('Social.back')}
						</Button>
					)}
					<div className="chat-box-screen bg-white flex-1">
						<Chat
							refetch={refetch}
							receiverDetails={receiverDetails}
							isBlock={isBlock}
							blockUserIds={blockUserIds}
							isReported={isReported}
						/>

						<div className="chat-box-screen-body">
							<ChatMessages
								receiverDetails={receiverDetails}
								setCurrentPage={setCurrentPage}
								metaData={meta}
							/>
							{isBlock || isReported ? (
								<div className="text-center blocked-chat-msg mt-4">
									{checkBlockBySelf()
										? translate('Extras.YouBlockedUser')
										: translate('Extras.unableSendReceiveMessages')}
								</div>
							) : (
								<MessageForm isBlock={isBlock} />
							)}
						</div>
					</div>
				</div>
			</Container>
		</div>
	);
};

export default ChatBox;
